import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MailCheck } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/Common";
import { api, errMsg } from "@/lib/api";

export const AutoFollowUp = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cap, setCap] = useState("");
  useEffect(() => { api.get("/admin/resume-scans/auto-follow-up").then((r) => { setS(r.data); setCap(String(r.data?.daily_cap ?? 0)); }).catch(() => {}); }, []);
  if (!s) return null;
  const save = async (patch) => {
    const next = { ...s, ...patch };
    setBusy(true);
    try {
      const body = { enabled: !!next.enabled, daily_cap: Number(next.daily_cap) };
      if (next.nudge_enabled != null) body.nudge_enabled = next.nudge_enabled;
      const { data } = await api.put("/admin/resume-scans/auto-follow-up", body);
      const saved = { ...next, ...(data || {}) };
      setS(saved); setCap(String(saved.daily_cap ?? 0));
      toast.success("Auto follow-up saved");
    } catch (e) { setCap(String(s.daily_cap ?? 0)); toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const saveCap = () => {
    const n = Number(cap);
    if (cap.trim() === "" || !Number.isInteger(n) || n < 1 || n > 200) { setCap(String(s.daily_cap ?? 0)); toast.error("Daily cap must be a whole number from 1 to 200"); return; }
    if (n !== s.daily_cap) save({ daily_cap: n });
  };
  const paused = !!s.replaced_by_sequence;
  const state = paused ? "Paused" : s.enabled ? "On" : "Off";
  return (
    <Card className="mb-6 flex flex-col gap-4 md:flex-row md:items-center" data-testid="auto-followup-card">
      <MailCheck className="h-6 w-6 shrink-0 text-[#D4AF37]" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-sm font-medium text-zinc-100">Auto follow-up email
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${paused ? "bg-amber-500/20 text-amber-300" : s.enabled ? "bg-emerald-500/20 text-emerald-300" : "bg-zinc-800 text-zinc-400"}`} data-testid="auto-followup-state">{state}</span>
        </div>
        <p className="mt-0.5 text-xs text-zinc-400" data-testid="auto-followup-note">
          {paused ? <>Paused: an approved email series is emailing scan leads instead. <Link to="/admin/mk" className="text-[#F3C649] underline underline-offset-2">Manage in Email Marketing</Link></> : `Sends each new scan lead a personal follow-up ${s.delay_hours ?? 24}h after the scan${s.nudge_enabled ? `, plus a nudge after ${s.nudge_after_days ?? 4} days` : ""}, up to the daily cap.${s.sent_today != null ? ` Sent today: ${s.sent_today}.` : ""}`}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-xs text-zinc-400">Daily cap
          <Input type="number" min="1" max="200" value={cap} onChange={(e) => setCap(e.target.value)} disabled={busy || paused} onBlur={saveCap} className="h-8 w-20" data-testid="auto-followup-cap" />
        </label>
        {s.nudge_enabled != null && <label className="flex items-center gap-2 text-xs text-zinc-400">Nudge<Switch checked={!!s.nudge_enabled} disabled={busy || paused} onCheckedChange={(v) => save({ nudge_enabled: v })} data-testid="auto-followup-nudge" /></label>}
        <label className="flex items-center gap-2 text-xs text-zinc-400">On<Switch checked={!!s.enabled} disabled={busy || paused} onCheckedChange={(v) => save({ enabled: v })} data-testid="auto-followup-switch" /></label>
      </div>
    </Card>
  );
};
